import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";
import { log } from "@/lib/logger";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then(r => setS(r.data)).catch((err) => log.error("load settings:", err));
    // `api` is a module-scoped axios instance and `setS` is a stable setter — mount-only fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!s) return <div className="text-white/60">Loading…</div>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...s };
      if (typeof body.states_served === "string") body.states_served = body.states_served.split(",").map(x => x.trim()).filter(Boolean);
      if (typeof body.certifications === "string") body.certifications = body.certifications.split(",").map(x => x.trim()).filter(Boolean);
      await api.put("/settings", body);
      toast.success("Settings saved");
    } catch (e) { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const arrToStr = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

  return (
    <div data-testid="admin-settings">
      <div className="eyebrow text-solar mb-2">Configuration</div>
      <h1 className="mb-2 text-3xl">Business Settings</h1>
      <p className="text-white/60 text-sm mb-6">Global business info used across the public site and emails.</p>

      <div className="border border-chrome bg-steel p-6 max-w-3xl space-y-4">
        <Row label="Business name"><Input value={s.business_name || ""} onChange={set("business_name")} /></Row>
        <Row label="Phone"><Input value={s.phone || ""} onChange={set("phone")} /></Row>
        <Row label="Email"><Input value={s.email || ""} onChange={set("email")} /></Row>
        <Row label="Headquarters"><Input value={s.headquarters || ""} onChange={set("headquarters")} /></Row>
        <Row label="Service hours"><Input value={s.service_hours || ""} onChange={set("service_hours")} /></Row>
        <Row label="Deployment estimate"><Input value={s.deployment_estimate || ""} onChange={set("deployment_estimate")} /></Row>
        <Row label="Storage duration"><Input value={s.storage_duration || ""} onChange={set("storage_duration")} /></Row>
        <Row label="States served (comma-separated)"><Input value={arrToStr(s.states_served)} onChange={(e) => setS({ ...s, states_served: e.target.value })} /></Row>
        <Row label="Certifications (comma-separated)"><Input value={arrToStr(s.certifications)} onChange={(e) => setS({ ...s, certifications: e.target.value })} /></Row>
        <Row label="Form notification email"><Input value={s.form_notification_email || ""} onChange={set("form_notification_email")} /></Row>
        <Row label='Sending "From" (e.g., 360 View LLC <admin@360viewlv.com>)'><Input value={s.from_email || ""} onChange={set("from_email")} /></Row>
        <Row label="Sending display name"><Input value={s.from_display_name || ""} onChange={set("from_display_name")} /></Row>
        <Row label="Reply-To on customer acknowledgments"><Input value={s.reply_to_email || ""} onChange={set("reply_to_email")} /></Row>
        <Row label="Monitoring options"><Textarea value={s.monitoring_options || ""} onChange={set("monitoring_options")} /></Row>
        <Row label="Emergency deployment language"><Textarea value={s.emergency_language || ""} onChange={set("emergency_language")} /></Row>
        <div className="pt-4 border-t border-chrome flex justify-end">
          <button data-testid="settings-save-btn" onClick={save} disabled={busy} className="btn-solar text-sm inline-flex items-center gap-2"><FloppyDisk size={14} weight="fill" /> {busy ? "Saving…" : "Save Settings"}</button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <label className="block">
    <div className="eyebrow text-white/70 mb-1.5">{label}</div>
    {children}
  </label>
);
const Input = (p) => <input {...p} className="w-full bg-obsidian border border-chrome px-3 py-2 text-sm text-white focus:border-solar outline-none" />;
const Textarea = (p) => <textarea rows={3} {...p} className="w-full bg-obsidian border border-chrome px-3 py-2 text-sm text-white focus:border-solar outline-none" />;
